/**
 * Interface that contains all information about the occupation
 * @property category: category of the occupation
 * @property occupationType: specific occupation from the category
 */
export interface Occupation {
  category: OccupationCategory;
  type: OccupationType;
}

// Represent all possible occupation categories
export enum OccupationCategory {
  Agriculture = 'agriculture',
  Art = 'art',
  Communication = 'communication',
  Construction = 'construction',
  CustomerService = 'customerService',
  Education = 'education',
  Engineering = 'engineering',
  Finance = 'finance',
  Law = 'law',
  Maintenance = 'maintenance',
  Medical = 'medical',
  Multimedia = 'multimedia',
  PublicCivilServices = 'publicCivilServices',
  Science = 'science',
  SocialServices = 'socialServices',
  Transport = 'transport',
}

// Represent all specific occupation types
export type OccupationType =
  | FinanceOccupation
  | MedicalOccupation
  | LawAndPublicOrder
  | PublicCivilService
  | DefaultOccupation;

// Used accross all categories
export enum DefaultOccupation {
  Other = 'other',
}

// Represent occupation types under the finance category
export enum FinanceOccupation {
  Accounting = 'accounting',
  Actuarial = 'actuarial',
  Banking = 'banking',
  Insurance = 'insurance',
  Other = 'other',
}

// Represent occupation types under the medical category
export enum MedicalOccupation {
  ClscStaff = 'clscStaff',
  HospitalStaff = 'hospitalStaff',
  Nurse = 'nurse',
  Technician = 'technician',
  Dentist = 'dentist',
  Doctor = 'doctor',
  Other = 'other',
}

// Represent occupation types under the law and public order category
export enum LawAndPublicOrder {
  Lawyer = 'lawyer',
  Notary = 'notary',
  PoliceOfficer = 'policeOfficer',
  Other = 'other',
}

// Represent occupation types under the public civil service category
export enum PublicCivilService {
  FederalGovernment = 'federalGovernment',
  MunicipalGovernment = 'municipalGovernment',
  ProvincialGovernment = 'provincialGovernment',
  Other = 'other',
}

// Map category to a list of possible occupation type
export const OccupationCategoryMapper: Map<OccupationCategory, OccupationType[]> = new Map([
  [OccupationCategory.Agriculture, [DefaultOccupation.Other]],
  [OccupationCategory.Art, [DefaultOccupation.Other]],
  [OccupationCategory.Communication, [DefaultOccupation.Other]],
  [OccupationCategory.Construction, [DefaultOccupation.Other]],
  [OccupationCategory.CustomerService, [DefaultOccupation.Other]],
  [OccupationCategory.Education, [DefaultOccupation.Other]],
  [OccupationCategory.Engineering, [DefaultOccupation.Other]],
  [
    OccupationCategory.Finance,
    [
      FinanceOccupation.Accounting,
      FinanceOccupation.Actuarial,
      FinanceOccupation.Banking,
      FinanceOccupation.Insurance,
      FinanceOccupation.Other,
    ],
  ],
  [
    OccupationCategory.Law,
    [LawAndPublicOrder.Lawyer, LawAndPublicOrder.Notary, LawAndPublicOrder.PoliceOfficer, LawAndPublicOrder.Other],
  ],
  [OccupationCategory.Maintenance, [DefaultOccupation.Other]],
  [
    OccupationCategory.Medical,
    [
      MedicalOccupation.ClscStaff,
      MedicalOccupation.HospitalStaff,
      MedicalOccupation.Nurse,
      MedicalOccupation.Technician,
      MedicalOccupation.Dentist,
      MedicalOccupation.Doctor,
      MedicalOccupation.Other,
    ],
  ],
  [OccupationCategory.Multimedia, [DefaultOccupation.Other]],
  [
    OccupationCategory.PublicCivilServices,
    [
      PublicCivilService.FederalGovernment,
      PublicCivilService.MunicipalGovernment,
      PublicCivilService.ProvincialGovernment,
      PublicCivilService.Other,
    ],
  ],
  [OccupationCategory.Science, [DefaultOccupation.Other]],
  [OccupationCategory.SocialServices, [DefaultOccupation.Other]],
  [OccupationCategory.Transport, [DefaultOccupation.Other]],
]);
